<template>
  <div class="live-monitor">
    <div class="breadcrumb">
      <Breadcrumb>
        <Breadcrumb-item>约课管理</Breadcrumb-item>
        <Breadcrumb-item>实时监课</Breadcrumb-item>
      </Breadcrumb>
    </div>
    <div class="lm-toolbar">
      <input class="lm-autofill-trap" tabindex="-1" autocomplete="off" name="lm-autofill-trap">
      <span class="lm-field">
        <span class="lm-label">学生</span>
        <Select
          ref="stuSelect"
          v-model="selectedUids"
          filterable
          remote
          multiple
          clearable
          transfer
          :remote-method="remoteStudent"
          :loading="studentLoading"
          placeholder="学生姓名 / 手机号 / ID，可多选"
          style="width: 360px"
          @on-change="onStudentChange">
          <Option v-for="item in studentOptions" :value="item.id" :key="item.id">
            {{ item.id }} - {{ item.nickname }} - {{ item.mobile }}
          </Option>
        </Select>
      </span>
      <span class="lm-field">
        <span class="lm-label">课程类型</span>
        <Select
          v-model.number="lsnType"
          clearable
          transfer
          placeholder="全部"
          style="width: 140px"
          @on-change="onLsnTypeChange">
          <Option :value="2">常规课</Option>
          <Option :value="1">体验课</Option>
        </Select>
      </span>
      <span class="lm-field">
        <span class="lm-label">老师</span>
        <Input
          ref="tchInput"
          v-model="tchKey"
          name="lmTchKey"
          autocomplete="off"
          clearable
          placeholder="老师 ID / 姓名，回车搜索"
          style="width: 220px"
          @on-enter="fetchLessons"
          @on-clear="fetchLessons"></Input>
      </span>
      <span class="lm-field" v-if="canFilterMine">
        <span class="lm-label">只看我的学生</span>
        <i-switch v-model="onlyMine" @on-change="onOnlyMineChange"></i-switch>
      </span>
      <span class="lm-field">
        <span class="lm-label">进入自动播放</span>
        <i-switch v-model="autoPlay" @on-change="onAutoPlayChange"></i-switch>
      </span>
      <span class="lm-field">
        <span class="lm-label">同时播放数</span>
        <InputNumber
          v-model="playLimit"
          :min="1"
          :max="playLimitMax"
          style="width: 80px"
          @on-change="onPlayLimitChange"></InputNumber>
      </span>
      <span class="lm-field">
        <span class="lm-label">一行几个</span>
        <Select
          v-model.number="colsPerRow"
          transfer
          style="width: 110px"
          @on-change="onColsChange">
          <Option :value="0">自适应</Option>
          <Option :value="1">1</Option>
          <Option :value="2">2</Option>
          <Option :value="3">3</Option>
          <Option :value="4">4</Option>
          <Option :value="5">5</Option>
          <Option :value="6">6</Option>
        </Select>
      </span>
      <span class="lm-field" v-if="hiddenLessons.length">
        <Dropdown trigger="click" transfer @on-click="onHiddenClick">
          <a href="javascript:void(0)">
            未展示 {{ hiddenLessons.length }}
            <Icon type="arrow-down-b"></Icon>
          </a>
          <Dropdown-menu slot="list">
            <Dropdown-item v-for="item in hiddenLessons" :name="String(item.id)" :key="item.id">
              {{ hiddenLabel(item) }}
            </Dropdown-item>
          </Dropdown-menu>
        </Dropdown>
      </span>
      <span class="lm-field">
        <Tag v-for="item in hiddenLessons.slice(0, 3)" :key="'tag-' + item.id" @click.native="replaceWithLesson(item)">
          {{ item.userNickname || item.uid }}
        </Tag>
        <span v-if="hiddenLessons.length > 3" class="lm-more">等 {{ hiddenLessons.length }} 堂未展示</span>
      </span>
      <span class="lm-field">
        <Button @click="resetLayout">重置布局</Button>
        <Button type="primary" :loading="loading" icon="refresh" @click="fetchLessons">刷新</Button>
      </span>
    </div>
    <div class="lm-hint">可监课时间：体验课课前 {{ trialBeforeMin }} 分钟、课后 {{ trialAfterMin }} 分钟；常规课课前 {{ regularBeforeMin }} 分钟、课后 {{ regularAfterMin }} 分钟</div>
    <div v-if="!loading && !allLessons.length" class="lm-empty">当前没有可监课的课程</div>
    <div ref="canvas" class="lm-canvas" :style="{minHeight: canvasMinHeight + 'px'}">
      <live-monitor-window
        v-for="win in windows"
        :key="win.id"
        :win="win"
        @play="playWin"
        @pause="pauseWin"
        @focus="focusWin"
        @maximize="maximizeWin"
        @restore="restoreWin"
        @open-external="openExternal">
      </live-monitor-window>
    </div>
  </div>
</template>
<script>
import LiveMonitorWindow from './LiveMonitorWindow'

const AUTO_PLAY_KEY = 'bops.liveMonitor.autoPlay'
const PLAY_LIMIT_KEY = 'bops.liveMonitor.playLimit'
const COLS_KEY = 'bops.liveMonitor.colsPerRow'
const WIN_W = 360
const WIN_H = 280
const MARGIN = 12
const MAX_Z = 300
const QUERY_LOOKBACK_SEC = 7200

export default {
  name: 'LiveMonitor',
  components: {
    LiveMonitorWindow
  },
  data: function () {
    return {
      selectedUids: [],
      selectedStudents: [],
      studentOptions: [],
      studentLoading: false,
      lsnType: null,
      tchKey: '',
      onlyMine: false,
      autoPlay: true,
      playLimit: 1,
      trialBeforeMin: 3,
      trialAfterMin: 1,
      regularBeforeMin: 1,
      regularAfterMin: 1,
      colsPerRow: 0,
      loading: false,
      allLessons: [],
      windows: [],
      topZ: MAX_Z,
      playSeq: 0,
      resizeTimer: null,
      canvasMinHeight: 400
    }
  },
  computed: {
    hiddenLessons: function () {
      let shown = {}
      this.windows.forEach(function (win) {
        shown[win.id] = true
      })
      return this.allLessons.filter(function (lesson) {
        return !shown[lesson.id]
      })
    },
    canFilterMine: function () {
      let rule = Number(this.$store.get('adminRule'))
      return rule === 1 || rule === 2
    },
    playLimitMax: function () {
      let n = this.windows.length
      if (n < 1) {
        return 9
      }
      return Math.min(9, n)
    },
    effectivePlayLimit: function () {
      let n = this.playLimit
      if (!n || n < 1) {
        n = 1
      }
      if (n > 9) {
        n = 9
      }
      if (this.windows.length > 0 && n > this.windows.length) {
        n = this.windows.length
      }
      return n
    }
  },
  mounted: function () {
    this.loadSettings()
    window.addEventListener('resize', this.onResize)
    let self = this
    this.$nextTick(function () {
      self.disableSearchAutofill()
      self.loadWindowConfig()
    })
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.onResize)
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
  },
  methods: {
    disableSearchAutofill: function () {
      let nodes = []
      let stu = this.$refs.stuSelect
      if (stu && stu.$el) {
        nodes = nodes.concat(Array.prototype.slice.call(stu.$el.querySelectorAll('input')))
      }
      let tch = this.$refs.tchInput
      if (tch && tch.$el) {
        nodes = nodes.concat(Array.prototype.slice.call(tch.$el.querySelectorAll('input')))
      }
      nodes.forEach(function (el, index) {
        el.setAttribute('autocomplete', 'off')
        el.setAttribute('autocorrect', 'off')
        el.setAttribute('autocapitalize', 'off')
        el.setAttribute('spellcheck', 'false')
        el.setAttribute('name', 'lm-search-' + index)
        el.setAttribute('readonly', 'readonly')
        el.addEventListener('focus', function () {
          el.removeAttribute('readonly')
        })
      })
    },
    loadSettings: function () {
      let auto = localStorage.getItem(AUTO_PLAY_KEY)
      if (auto === '0' || auto === 'false') {
        this.autoPlay = false
      } else {
        this.autoPlay = true
      }
      let limit = parseInt(localStorage.getItem(PLAY_LIMIT_KEY), 10)
      if (isNaN(limit) || limit < 1) {
        this.playLimit = 1
      } else if (limit > 9) {
        this.playLimit = 9
      } else {
        this.playLimit = limit
      }
      let cols = parseInt(localStorage.getItem(COLS_KEY), 10)
      if (isNaN(cols) || cols < 1) {
        this.colsPerRow = 0
      } else if (cols > 6) {
        this.colsPerRow = 6
      } else {
        this.colsPerRow = cols
      }
    },
    persistAutoPlay: function () {
      localStorage.setItem(AUTO_PLAY_KEY, this.autoPlay ? '1' : '0')
    },
    persistPlayLimit: function () {
      localStorage.setItem(PLAY_LIMIT_KEY, String(this.playLimit))
    },
    persistCols: function () {
      localStorage.setItem(COLS_KEY, String(this.colsPerRow || 0))
    },
    contentRect: function () {
      let el = this.$refs.canvas
      if (el && el.clientWidth > 80 && el.clientHeight > 80) {
        return {
          width: el.clientWidth,
          height: el.clientHeight
        }
      }
      return {
        width: Math.max(WIN_W, window.innerWidth - 282),
        height: Math.max(WIN_H, window.innerHeight - 220)
      }
    },
    calcGrid: function (itemCount) {
      let area = this.contentRect()
      let cellW = WIN_W
      let cellH = WIN_H
      let cols = Math.max(1, Math.floor((area.width - MARGIN) / WIN_W))
      let fixed = parseInt(this.colsPerRow, 10)
      if (fixed >= 1 && fixed <= 6) {
        cols = fixed
        cellW = Math.max(240, Math.floor((area.width - MARGIN) / cols))
        cellH = Math.round(cellW * WIN_H / WIN_W)
      }
      let n = itemCount != null ? itemCount : this.allLessons.length
      if (n < 1) {
        n = 1
      }
      let rows = Math.max(1, Math.ceil(n / cols))
      return {
        cols: cols,
        rows: rows,
        cellW: cellW,
        cellH: cellH,
        slotCount: n,
        originLeft: MARGIN,
        originTop: MARGIN
      }
    },
    syncCanvasHeight: function (grid) {
      let rows = grid && grid.rows ? grid.rows : 1
      let cellH = grid && grid.cellH ? grid.cellH : WIN_H
      this.canvasMinHeight = rows * cellH + MARGIN * 2
    },
    gridRect: function (index, grid) {
      if (!grid) {
        grid = this.calcGrid()
      }
      let cols = grid.cols || 1
      let cellW = grid.cellW || WIN_W
      let cellH = grid.cellH || WIN_H
      return {
        left: grid.originLeft + (index % cols) * cellW,
        top: grid.originTop + Math.floor(index / cols) * cellH,
        width: cellW,
        height: cellH
      }
    },
    createWindow: function (lesson, rect) {
      this.topZ += 1
      return {
        id: lesson.id,
        lesson: lesson,
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        playing: false,
        maximized: false,
        restoreRect: null,
        zIndex: this.topZ,
        playStamp: 0
      }
    },
    monitorMinutes: function (value, fallback) {
      let n = parseInt(value, 10)
      if (isNaN(n) || n < 0) {
        return fallback
      }
      return n
    },
    applyWindowConfig: function (data) {
      if (!data) {
        return
      }
      this.trialBeforeMin = this.monitorMinutes(data.trialBeforeMin, 3)
      this.trialAfterMin = this.monitorMinutes(data.trialAfterMin, 1)
      this.regularBeforeMin = this.monitorMinutes(data.regularBeforeMin, 1)
      this.regularAfterMin = this.monitorMinutes(data.regularAfterMin, 1)
    },
    loadWindowConfig: function () {
      let self = this
      this.$http.get('/bookLesson/opened/monitorWindow').then(function (res) {
        self.applyWindowConfig(res.data)
        self.fetchLessons()
      }).catch(function () {
        self.fetchLessons()
      })
    },
    inMonitorWindow: function (lesson) {
      if (!lesson || !lesson.lsnBeginTime || !lesson.lsnEndTime) {
        return false
      }
      let now = this.$moment().unix()
      let trial = Number(lesson.lsnType) === 1
      let beforeMin = trial ? this.trialBeforeMin : this.regularBeforeMin
      let afterMin = trial ? this.trialAfterMin : this.regularAfterMin
      let before = beforeMin * 60
      let after = afterMin * 60
      return now >= lesson.lsnBeginTime - before && now <= lesson.lsnEndTime + after
    },
    fetchLessons: function () {
      let self = this
      let now = this.$moment()
      let params = {
        startTime: now.clone().subtract(QUERY_LOOKBACK_SEC, 'seconds').format('X'),
        endTime: now.clone().add(Math.max(this.trialBeforeMin, this.regularBeforeMin) * 60, 'seconds').format('X'),
        lsnSelStatusList: [1, 2, 3],
        pageIndex: 1,
        pageSize: 300
      }
      if (this.selectedUids && this.selectedUids.length) {
        params.uidList = this.selectedUids
      }
      if (this.lsnType === 1 || this.lsnType === 2) {
        params.lsnType = this.lsnType
      }
      let tchKey = this.tchKey ? String(this.tchKey).trim() : ''
      if (tchKey) {
        if (/^\d+$/.test(tchKey)) {
          params.tchId = parseInt(tchKey, 10)
        } else {
          params.tchName = tchKey
        }
      }
      if (this.onlyMine && this.canFilterMine) {
        let myId = parseInt(this.$store.get('id'), 10)
        let rule = Number(this.$store.get('adminRule'))
        if (myId > 0 && rule === 1) {
          params.adviserId = myId
        } else if (myId > 0 && rule === 2) {
          params.studyConsultantId = myId
        }
      }
      this.loading = true
      this.$http.post('/bookLesson/opened/list', params).then(function (res) {
        let list = []
        if (res.data && res.data.data) {
          list = res.data.data
        } else if (res.data && res.data.length) {
          list = res.data
        }
        self.allLessons = (list || []).filter(function (item) {
          return self.inMonitorWindow(item)
        })
        self.rebuildWindows(true)
        self.loading = false
      }).catch(function () {
        self.allLessons = []
        self.windows = []
        self.loading = false
      })
    },
    rebuildWindows: function (autoPlayNow) {
      let grid = this.calcGrid(this.allLessons.length)
      this.syncCanvasHeight(grid)
      let next = []
      for (let i = 0; i < this.allLessons.length; i++) {
        next.push(this.createWindow(this.allLessons[i], this.gridRect(i, grid)))
      }
      this.windows = next
      this.clampPlayLimit()
      if (autoPlayNow && this.autoPlay) {
        this.autoPlayWindows()
      }
    },
    applySlotCount: function () {
      if (!this.windows.length) {
        return
      }
      let grid = this.calcGrid(this.windows.length)
      this.syncCanvasHeight(grid)
      let self = this
      this.windows.forEach(function (win, index) {
        if (win.maximized) {
          return
        }
        let rect = self.gridRect(index, grid)
        win.left = rect.left
        win.top = rect.top
        win.width = rect.width
        win.height = rect.height
      })
    },
    autoPlayWindows: function () {
      let limit = this.effectivePlayLimit
      let started = 0
      for (let i = 0; i < this.windows.length && started < limit; i++) {
        if (this.windows[i].lesson && this.windows[i].lesson.liveHls) {
          this.startPlay(this.windows[i])
          started += 1
        }
      }
    },
    playingCount: function () {
      let n = 0
      this.windows.forEach(function (win) {
        if (win.playing) {
          n += 1
        }
      })
      return n
    },
    startPlay: function (win) {
      if (!win || !win.lesson || !win.lesson.liveHls) {
        return
      }
      this.playSeq += 1
      win.playing = true
      win.playStamp = this.playSeq
    },
    pauseOldest: function () {
      let oldest = null
      this.windows.forEach(function (win) {
        if (!win.playing) {
          return
        }
        if (!oldest || win.playStamp < oldest.playStamp) {
          oldest = win
        }
      })
      if (oldest) {
        oldest.playing = false
        oldest.playStamp = 0
      }
    },
    enforcePlayLimit: function () {
      while (this.playingCount() > this.effectivePlayLimit) {
        this.pauseOldest()
      }
    },
    clampPlayLimit: function () {
      this.enforcePlayLimit()
    },
    playWin: function (win) {
      if (!win.lesson || !win.lesson.liveHls) {
        return
      }
      if (win.playing) {
        return
      }
      if (this.playingCount() >= this.effectivePlayLimit) {
        this.pauseOldest()
      }
      this.startPlay(win)
      this.focusWin(win)
    },
    pauseWin: function (win) {
      win.playing = false
      win.playStamp = 0
    },
    focusWin: function (win) {
      this.topZ += 1
      win.zIndex = this.topZ
    },
    maximizeWin: function (win) {
      if (win.maximized) {
        return
      }
      win.restoreRect = {
        left: win.left,
        top: win.top,
        width: win.width,
        height: win.height
      }
      let area = this.contentRect()
      win.left = 0
      win.top = 0
      win.width = Math.max(WIN_W, area.width)
      win.height = Math.max(WIN_H, area.height)
      win.maximized = true
      this.focusWin(win)
    },
    restoreWin: function (win) {
      if (!win.maximized) {
        return
      }
      let rect = win.restoreRect
      if (rect) {
        win.left = rect.left
        win.top = rect.top
        win.width = rect.width
        win.height = rect.height
      }
      win.maximized = false
      win.restoreRect = null
    },
    openExternal: function (win) {
      if (win.lesson && win.lesson.liveUrl) {
        window.open(win.lesson.liveUrl)
      }
    },
    resetLayout: function () {
      let grid = this.calcGrid(this.windows.length)
      this.syncCanvasHeight(grid)
      let self = this
      this.windows.forEach(function (win, index) {
        let rect = self.gridRect(index, grid)
        win.left = rect.left
        win.top = rect.top
        win.width = rect.width
        win.height = rect.height
        win.maximized = false
        win.restoreRect = null
      })
    },
    hiddenLabel: function (lesson) {
      let name = lesson.userNickname || '学生'
      let uid = lesson.uid != null ? lesson.uid : ''
      let title = lesson.lessonTitle || ''
      return name + '(' + uid + ') ' + title
    },
    onHiddenClick: function (name) {
      let id = parseInt(name, 10)
      let lesson = null
      this.hiddenLessons.forEach(function (item) {
        if (item.id === id) {
          lesson = item
        }
      })
      if (lesson) {
        this.replaceWithLesson(lesson)
      }
    },
    replaceWithLesson: function (lesson) {
      if (!lesson) {
        return
      }
      let grid = this.calcGrid(this.windows.length + 1)
      this.syncCanvasHeight(grid)
      if (this.hiddenLessons.length) {
        this.windows.push(this.createWindow(lesson, this.gridRect(this.windows.length, grid)))
        this.clampPlayLimit()
        return
      }
      let idx = -1
      for (let i = this.windows.length - 1; i >= 0; i--) {
        if (!this.windows[i].playing) {
          idx = i
          break
        }
      }
      if (idx < 0) {
        idx = this.windows.length - 1
      }
      if (idx < 0) {
        this.windows.push(this.createWindow(lesson, this.gridRect(0, grid)))
        this.clampPlayLimit()
        return
      }
      let old = this.windows[idx]
      let rect = {
        left: old.left,
        top: old.top,
        width: old.width,
        height: old.height
      }
      this.windows.splice(idx, 1, this.createWindow(lesson, rect))
      this.clampPlayLimit()
    },
    remoteStudent: function (query) {
      query = query === null || query === undefined ? '' : String(query).trim()
      if (!query) {
        this.studentOptions = this.selectedStudents.slice()
        return
      }
      let self = this
      this.studentLoading = true
      this.$http.get('/user/listByCondition', {params: {infoKeyword: query}}).then(function (res) {
        let list = res.data || []
        let merged = list.slice()
        self.selectedStudents.forEach(function (stu) {
          let exists = merged.some(function (item) {
            return item.id === stu.id
          })
          if (!exists) {
            merged.unshift(stu)
          }
        })
        self.studentOptions = merged
        self.studentLoading = false
      }).catch(function () {
        self.studentLoading = false
      })
    },
    onStudentChange: function (uids) {
      let ids = uids || []
      this.selectedStudents = this.studentOptions.filter(function (item) {
        return ids.indexOf(item.id) >= 0
      })
      this.fetchLessons()
    },
    onLsnTypeChange: function () {
      this.fetchLessons()
    },
    onOnlyMineChange: function () {
      this.fetchLessons()
    },
    onAutoPlayChange: function (val) {
      this.autoPlay = val
      this.persistAutoPlay()
    },
    onColsChange: function () {
      this.persistCols()
      this.resetLayout()
    },
    onPlayLimitChange: function (val) {
      let n = parseInt(val, 10)
      if (isNaN(n) || n < 1) {
        n = 1
      }
      if (n > this.playLimitMax) {
        n = this.playLimitMax
      }
      this.playLimit = n
      this.persistPlayLimit()
      this.enforcePlayLimit()
    },
    onResize: function () {
      let self = this
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      this.resizeTimer = setTimeout(function () {
        self.applySlotCount()
      }, 150)
    }
  }
}
</script>
<style scoped>
.lm-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 8px;
}
.lm-field {
  display: inline-flex;
  align-items: center;
  margin: 0 16px 12px 0;
}
.lm-label {
  margin-right: 8px;
  color: #495060;
  white-space: nowrap;
}
.lm-more {
  color: #80848f;
  font-size: 12px;
}
.lm-hint {
  margin: 0 0 8px;
  padding: 6px 10px;
  color: #495060;
  font-size: 12px;
  background: #f8f8f9;
  border: 1px solid #e8eaec;
  border-radius: 3px;
}
.lm-empty {
  margin-top: 48px;
  text-align: center;
  color: #80848f;
}
.lm-canvas {
  position: relative;
}
.lm-autofill-trap {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// LiveMonitor.vue?031a2f05